import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { HoldAttachmentDraft, HoldCollection, HoldGroup, HoldPayload } from '@holdthat/core';

export * from './encrypted';

export type OutboxKind = 'hold' | 'attachment' | 'collection';

export interface OutboxOperation {
  operationId: string;
  kind: OutboxKind;
  entityId: string;
  holdId?: string;
  createdAt: string;
  attempts: number;
  lastError?: string;
}

interface HoldThatDatabase extends DBSchema {
  holds: { key: string; value: HoldPayload; indexes: { 'by-updated': string } };
  collections: { key: string; value: HoldCollection; indexes: { 'by-name': string } };
  groups: { key: string; value: HoldGroup; indexes: { 'by-name': string } };
  blobs: { key: string; value: Blob };
  outbox: { key: string; value: OutboxOperation; indexes: { 'by-kind': OutboxKind; 'by-created': string } };
  meta: { key: string; value: unknown };
}

const DATABASE_VERSION = 1;

export class LocalRepository {
  private database?: IDBPDatabase<HoldThatDatabase>;

  constructor(public readonly userId: string) {}

  private get name() { return `holdthat-web-${this.userId.replace(/[^a-zA-Z0-9_-]/g, '_')}`; }

  private async db() {
    if (!this.database) {
      this.database = await openDB<HoldThatDatabase>(this.name, DATABASE_VERSION, {
        upgrade(database) {
          const holds = database.createObjectStore('holds', { keyPath: 'id' });
          holds.createIndex('by-updated', 'updatedAt');
          const collections = database.createObjectStore('collections', { keyPath: 'id' });
          collections.createIndex('by-name', 'name');
          const groups = database.createObjectStore('groups', { keyPath: 'id' });
          groups.createIndex('by-name', 'name');
          database.createObjectStore('blobs');
          const outbox = database.createObjectStore('outbox', { keyPath: 'operationId' });
          outbox.createIndex('by-kind', 'kind');
          outbox.createIndex('by-created', 'createdAt');
          database.createObjectStore('meta');
        },
      });
    }
    return this.database;
  }

  async holds() { return (await this.db()).getAll('holds'); }
  async collections() { return (await this.db()).getAll('collections'); }
  async groups() { return (await this.db()).getAll('groups'); }
  async outbox() { return (await this.db()).getAllFromIndex('outbox', 'by-created'); }
  async blob(key: string) { return (await this.db()).get('blobs', key); }
  async meta<T>(key: string) { return (await this.db()).get('meta', key) as Promise<T | undefined>; }
  async setMeta(key: string, value: unknown) { await (await this.db()).put('meta', value, key); }

  async saveHold(hold: HoldPayload, drafts: HoldAttachmentDraft[] = []) {
    const database = await this.db();
    const transaction = database.transaction(['holds', 'blobs', 'outbox'], 'readwrite');
    const local = { ...hold, needsSync: true, updatedAt: new Date().toISOString() };
    await transaction.objectStore('holds').put(local);
    await transaction.objectStore('outbox').put(operation('hold', hold.id));
    for (const draft of drafts) {
      await transaction.objectStore('blobs').put(draft.blob, blobKey(hold.id, draft.metadata.id));
      await transaction.objectStore('outbox').put(operation('attachment', draft.metadata.id, hold.id));
    }
    await transaction.done;
    return local;
  }

  async saveCollection(collection: HoldCollection) {
    const database = await this.db();
    const transaction = database.transaction(['collections', 'outbox'], 'readwrite');
    const local = { ...collection, needsSync: true, updatedAt: new Date().toISOString() };
    await transaction.objectStore('collections').put(local);
    await transaction.objectStore('outbox').put(operation('collection', local.id));
    await transaction.done;
    return local;
  }

  async mergeServer(holds: HoldPayload[], collections: HoldCollection[], groups: HoldGroup[]) {
    const database = await this.db();
    const transaction = database.transaction(['holds', 'collections', 'groups'], 'readwrite');
    for (const item of holds) {
      const local = await transaction.objectStore('holds').get(item.id);
      if (!local?.needsSync || new Date(item.updatedAt) >= new Date(local.updatedAt)) {
        await transaction.objectStore('holds').put({ ...item, needsSync: false });
      }
    }
    for (const item of collections) {
      const local = await transaction.objectStore('collections').get(item.id);
      if (!local?.needsSync || new Date(item.updatedAt) >= new Date(local.updatedAt)) {
        await transaction.objectStore('collections').put({ ...item, needsSync: false });
      }
    }
    for (const item of groups) await transaction.objectStore('groups').put(item);
    await transaction.done;
  }

  async confirmHolds(holds: HoldPayload[]) {
    const database = await this.db();
    const transaction = database.transaction(['holds', 'outbox'], 'readwrite');
    for (const item of holds) {
      await transaction.objectStore('holds').put({ ...item, needsSync: false });
      await transaction.objectStore('outbox').delete(operationId('hold', item.id));
    }
    await transaction.done;
  }

  async confirmCollection(collection: HoldCollection) {
    const database = await this.db();
    const transaction = database.transaction(['collections', 'outbox'], 'readwrite');
    await transaction.objectStore('collections').put({ ...collection, needsSync: false });
    await transaction.objectStore('outbox').delete(operationId('collection', collection.id));
    await transaction.done;
  }

  async confirmAttachment(holdId: string, attachmentId: string, remoteURLString?: string) {
    const database = await this.db();
    const transaction = database.transaction(['holds', 'outbox'], 'readwrite');
    const hold = await transaction.objectStore('holds').get(holdId);
    if (hold && remoteURLString) {
      await transaction.objectStore('holds').put({
        ...hold,
        attachments: hold.attachments.map((item) => item.id === attachmentId ? { ...item, remoteURLString } : item),
      });
    }
    await transaction.objectStore('outbox').delete(operationId('attachment', attachmentId));
    await transaction.done;
  }

  async removeLocalHold(id: string) {
    const database = await this.db();
    const hold = await database.get('holds', id);
    const transaction = database.transaction(['holds', 'blobs', 'outbox'], 'readwrite');
    await transaction.objectStore('holds').delete(id);
    await transaction.objectStore('outbox').delete(operationId('hold', id));
    for (const attachment of hold?.attachments ?? []) {
      await transaction.objectStore('blobs').delete(blobKey(id, attachment.id));
      await transaction.objectStore('outbox').delete(operationId('attachment', attachment.id));
    }
    await transaction.done;
  }

  async recordFailure(item: OutboxOperation, error: unknown) {
    await (await this.db()).put('outbox', { ...item, attempts: item.attempts + 1, lastError: error instanceof Error ? error.message : String(error) });
  }

  close() { this.database?.close(); this.database = undefined; }
  async destroy() { this.close(); await deleteDB(this.name); }
}

export function blobKey(holdId: string, attachmentId: string) { return `${holdId}:${attachmentId}`; }
export function operationId(kind: OutboxKind, entityId: string) { return `${kind}:${entityId}`; }
function operation(kind: OutboxKind, entityId: string, holdId?: string): OutboxOperation {
  return { operationId: operationId(kind, entityId), kind, entityId, holdId, createdAt: new Date().toISOString(), attempts: 0 };
}
